import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API_BASE_URL } from "../../config";
import ImageUpload from "../../Components/ImageUpload";
import CategoryPricingPicker from "../../Components/CategoryPricingPicker";
import RestaurantSelect from "../../Components/RestaurantSelect";
import { formatCategoryLabel } from "../../constants/categories";
import { downloadExport } from "../../utils/downloadExport";

const SORT_OPTIONS = [
  { value: "createdAt-desc", label: "Newest first" },
  { value: "createdAt-asc", label: "Oldest first" },
  { value: "title-asc", label: "Name (A–Z)" },
  { value: "title-desc", label: "Name (Z–A)" },
  { value: "price-asc", label: "Price (low–high)" },
  { value: "price-desc", label: "Price (high–low)" },
];

const UpdateItem = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("createdAt-desc");
  const [restaurantFilter, setRestaurantFilter] = useState("");
  const [restaurants, setRestaurants] = useState([]);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    desc: "",
    restaurants: [],
    categoryPricing: [],
    availability: false,
    popular: false,
    imageUrl: [],
  });
  const [selectedItem, setSelectedItem] = useState(null);
  const [error, setError] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const token = localStorage.getItem("authToken");

  const handleExport = async () => {
    setExporting(true);
    try {
      await downloadExport(`${API_BASE_URL}/menu/export`, token, "items-export.xlsx");
    } catch (err) {
      console.error("Error exporting items:", err);
      alert("Failed to export items.");
    } finally {
      setExporting(false);
    }
  };

  const fetchItems = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const [sortBy, sortDir] = sort.split("-");
      const response = await axios.get(`${API_BASE_URL}/menu/items`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          page,
          limit: 24,
          search: search || undefined,
          sortBy,
          sortDir,
          restaurant: restaurantFilter || undefined,
        },
      });
      setItems(response.data.items);
      setPagination(response.data.pagination);
    } catch (err) {
      console.error("Error fetching items:", err);
      setLoadError("Couldn't load items. Check your connection and try again.");
      setItems([]);
      setPagination(null);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, search, sort, restaurantFilter]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  useEffect(() => {
    axios
      .get(`${API_BASE_URL}/restaurant?all=true`)
      .then((res) => setRestaurants(res.data.restaurants))
      .catch((err) => console.error("Error fetching restaurants:", err));
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({ ...formData, [name]: type === "checkbox" ? checked : value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (formData.restaurants.length === 0) {
      setError("Pick at least one restaurant.");
      return;
    }
    if (formData.categoryPricing.length === 0) {
      setError("Pick at least one category and set its price.");
      return;
    }
    const categoryPricing = formData.categoryPricing.map((e) => ({
      category: e.category,
      price: Number(e.price),
    }));
    if (categoryPricing.some((e) => Number.isNaN(e.price) || e.price <= 0)) {
      setError("Every selected category needs a positive price.");
      return;
    }
    if (formData.imageUrl.length === 0) {
      setError("Keep at least one image.");
      return;
    }

    setSubmitting(true);
    try {
      await axios.put(
        `${API_BASE_URL}/menu/update-item/${selectedItem._id}`,
        { ...formData, categoryPricing },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSelectedItem(null);
      await fetchItems();
    } catch (err) {
      console.error("Error updating item:", err);
      setError(err.response?.data?.message || "Failed to update item.");
    } finally {
      setSubmitting(false);
    }
  };

  const openModal = (item) => {
    setError(null);
    setSelectedItem(item);
    setFormData({
      title: item.title,
      desc: item.desc,
      restaurants: (item.restaurants || []).map((r) => r._id || r),
      categoryPricing: item.categoryPricing.map((cp) => ({ category: cp.category, price: cp.price })),
      availability: item.availability,
      popular: item.popular || false,
      imageUrl: [...item.imageUrl],
    });
  };

  const handleDelete = async (itemId) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this item?"
    );
    if (!confirmDelete) return;

    try {
      await axios.delete(`${API_BASE_URL}/menu/delete-item/${itemId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      await fetchItems();
    } catch (err) {
      console.error("Error deleting item:", err);
      alert(err.response?.data?.message || "Failed to delete item.");
    }
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-2xl font-bold text-ink">Items List</h2>
        <button
          onClick={handleExport}
          disabled={exporting}
          className="text-sm font-semibold px-3 py-1.5 border border-line rounded hover:bg-paper transition disabled:opacity-50"
        >
          {exporting ? "Exporting…" : "Export to Excel"}
        </button>
      </div>

      <div className="flex flex-wrap gap-3 mb-6">
        <input
          placeholder="Search by title or category…"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          className="flex-1 min-w-[220px] p-2 border border-line rounded"
        />
        <select
          value={sort}
          onChange={(e) => {
            setSort(e.target.value);
            setPage(1);
          }}
          className="p-2 border border-line rounded"
        >
          {SORT_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <select
          value={restaurantFilter}
          onChange={(e) => {
            setRestaurantFilter(e.target.value);
            setPage(1);
          }}
          className="p-2 border border-line rounded"
        >
          <option value="">All restaurants</option>
          {restaurants.map((r) => (
            <option key={r._id} value={r._id}>
              {r.name}
            </option>
          ))}
        </select>
      </div>

      {loadError && (
        <div className="flex items-center justify-between gap-3 mb-4 p-3 border border-red-200 bg-red-50 text-red-700 text-sm rounded">
          <span>{loadError}</span>
          <button onClick={fetchItems} className="font-semibold underline shrink-0">
            Retry
          </button>
        </div>
      )}

      {loading ? (
        <p className="text-sub">Loading items…</p>
      ) : loadError ? null : items.length === 0 ? (
        <p className="text-sub">No items match this search.</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((item) => (
            <div key={item._id} className="bg-card border border-line rounded-lg p-4">
              <img
                src={item.imageUrl[0]}
                alt={item.title}
                className="w-full h-40 object-cover rounded mb-2"
              />
              <h3 className="text-lg font-semibold text-ink">{item.title}</h3>
              {item.restaurants?.length > 0 && (
                <p className="text-xs text-primary font-semibold uppercase tracking-wide">
                  {item.restaurants.map((r) => r.name).join(", ")}
                  {item.popular && " · ⭐ Popular"}
                </p>
              )}
              <ul className="text-sm text-sub space-y-0.5">
                {item.categoryPricing?.map((cp) => (
                  <li key={cp.category} className="flex justify-between">
                    <span>{formatCategoryLabel(cp.category)}</span>
                    <span className="font-mono text-ink">৳{cp.price}</span>
                  </li>
                ))}
              </ul>
              {!item.availability && (
                <span className="inline-block mt-1 text-xs px-2 py-0.5 rounded-full bg-red-50 text-red-700">
                  Unavailable
                </span>
              )}
              <div className="flex justify-between mt-4">
                <button
                  onClick={() => openModal(item)}
                  className="bg-primary text-white py-2 px-4 rounded hover:opacity-90 transition"
                >
                  Update
                </button>
                <button
                  onClick={() => handleDelete(item._id)}
                  className="bg-red-600 text-white py-2 px-4 rounded hover:bg-red-700 transition"
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {pagination && pagination.totalCount > 0 && (
        <div className="flex items-center gap-3 mt-6">
          <button
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
            className="px-3 py-1.5 border border-line rounded disabled:opacity-40"
          >
            Previous
          </button>
          <span className="text-sm text-sub">
            Page {pagination.page} of {pagination.totalPages} ({pagination.totalCount} items)
          </span>
          <button
            disabled={page >= pagination.totalPages}
            onClick={() => setPage((p) => p + 1)}
            className="px-3 py-1.5 border border-line rounded disabled:opacity-40"
          >
            Next
          </button>
        </div>
      )}

      {/* Form to Update Item */}
      {selectedItem && (
        <div className="fixed inset-0 bg-ink/60 flex justify-center items-center z-10 p-4 overflow-y-auto">
          <div className="bg-card p-6 rounded-lg border border-line w-full max-w-md my-8">
            <h2 className="text-xl font-semibold mb-4 text-ink">Update Item</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-2">
                  {error}
                </p>
              )}
              <input
                name="title"
                value={formData.title}
                onChange={handleChange}
                placeholder="Title"
                className="w-full p-2 border border-line rounded"
                required
              />
              <input
                name="desc"
                value={formData.desc}
                onChange={handleChange}
                placeholder="Description"
                className="w-full p-2 border border-line rounded"
                required
              />
              <RestaurantSelect
                value={formData.restaurants}
                onChange={(restaurants) => setFormData({ ...formData, restaurants })}
              />
              <CategoryPricingPicker
                value={formData.categoryPricing}
                onChange={(categoryPricing) => setFormData({ ...formData, categoryPricing })}
              />
              <ImageUpload
                multiple
                label="Images"
                value={formData.imageUrl}
                onChange={(imageUrl) => setFormData({ ...formData, imageUrl })}
              />
              <label className="flex items-center text-ink">
                <input
                  type="checkbox"
                  name="availability"
                  checked={formData.availability}
                  onChange={handleChange}
                  className="mr-2"
                />{" "}
                Available
              </label>
              <label className="flex items-center text-ink">
                <input
                  type="checkbox"
                  name="popular"
                  checked={formData.popular}
                  onChange={handleChange}
                  className="mr-2"
                />{" "}
                Popular
              </label>
              <button
                type="submit"
                disabled={submitting}
                className="w-full bg-primary text-white py-2 rounded hover:opacity-90 transition disabled:opacity-60"
              >
                {submitting ? "Saving…" : "Update Item"}
              </button>
              <button
                type="button"
                onClick={() => setSelectedItem(null)}
                className="w-full border border-line text-ink py-2 rounded hover:bg-paper transition"
              >
                Cancel
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UpdateItem;
